import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, TOKEN_KEY } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [session, setSession] = useState(undefined);

  const load = useCallback(async () => {
    if (!localStorage.getItem(TOKEN_KEY)) return setSession(null);
    try {
      const { data } = await api.get("/auth/me");
      setSession(data);
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      setSession(null);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const applyLogin = (data) => {
    localStorage.setItem(TOKEN_KEY, data.token);
    setSession({ user: data.user, tenant: data.tenant, impersonated_by: data.impersonated_by || null });
  };

  const login = async (phone, pin) => {
    const { data } = await api.post("/auth/login", { phone, pin });
    applyLogin(data);
    return data;
  };

  const impersonate = async (userId) => {
    const superToken = localStorage.getItem(TOKEN_KEY);
    const { data } = await api.post(`/platform/impersonate/${userId}`);
    localStorage.setItem("fv_super_token", superToken);
    applyLogin(data);
  };

  const endImpersonation = async () => {
    const superToken = localStorage.getItem("fv_super_token");
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    localStorage.removeItem("fv_super_token");
    if (superToken) { localStorage.setItem(TOKEN_KEY, superToken); await load(); } else { setSession(null); }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem("fv_super_token");
    setSession(null);
  };

  const role = session?.user?.role;
  const can = (...roles) => roles.includes(role);
  return <AuthContext.Provider value={{ session, user: session?.user, tenant: session?.tenant, role, can, login, logout, impersonate, endImpersonation, reload: load }}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
